const ACRONYMS = {
    api: 'API',
    fai: 'FAI',
    id: 'ID',
    ids: 'IDs',
    ipqc: 'IPQC',
    iqc: 'IQC',
    jo: 'JO',
    oee: 'OEE',
    oqc: 'OQC',
    po: 'PO',
    ppt: 'PPT',
    sku: 'SKU',
    so: 'SO',
    sql: 'SQL',
    uom: 'UOM',
    url: 'URL',
    utc: 'UTC',
    xlsx: 'XLSX',
};

const WORD_EXPANSIONS = {
    avg: 'average',
    desc: 'description',
    max: 'maximum',
    min: 'minimum',
    no: 'number',
    num: 'number',
    pct: 'percentage',
    qty: 'quantity',
};

const SNAKE_CASE_FIELD_PATTERN = /^[A-Za-z][A-Za-z0-9]*(?:_[A-Za-z0-9]+)+$/;
const SNAKE_CASE_TOKEN_PATTERN = /\b[A-Za-z][A-Za-z0-9]*(?:_[A-Za-z0-9]+)+\b/g;

function capitalize(value) {
    return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : '';
}

function formatToken(token, index) {
    const normalized = token.toLowerCase();
    if (ACRONYMS[normalized]) return ACRONYMS[normalized];

    const expanded = WORD_EXPANSIONS[normalized] || normalized;
    return index === 0 ? capitalize(expanded) : expanded;
}

export function humanizeFieldName(value) {
    const text = String(value == null ? '' : value);
    if (!SNAKE_CASE_FIELD_PATTERN.test(text)) return text;

    return text
        .split('_')
        .filter(Boolean)
        .map(formatToken)
        .join(' ');
}

export function humanizeSnakeCaseText(value) {
    return String(value == null ? '' : value)
        .replace(SNAKE_CASE_TOKEN_PATTERN, token => humanizeFieldName(token));
}

export function humanizeFileName(value) {
    const fileName = String(value == null ? '' : value);
    const extensionIndex = fileName.lastIndexOf('.');
    const stem = extensionIndex > 0 ? fileName.slice(0, extensionIndex) : fileName;
    const extension = extensionIndex > 0 ? fileName.slice(extensionIndex) : '';

    return `${humanizeFieldName(stem)}${extension}`;
}

function humanizeTitle(title) {
    if (typeof title === 'string') return humanizeSnakeCaseText(title);
    if (!title || typeof title !== 'object') return title;
    return Object.assign({}, title, {
        text: humanizeSnakeCaseText(title.text || ''),
    });
}

function humanizeAxis(axis) {
    if (!axis || typeof axis !== 'object') return axis;
    return Object.assign({}, axis, {
        title: humanizeTitle(axis.title),
    });
}

function isPlainObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function removeInvalidJsonEscapes(value) {
    return String(value == null ? '' : value)
        .replace(/\\(?!["\\/bfnrtu])/g, '');
}

export function parseChartJson(chartJson) {
    if (isPlainObject(chartJson)) return chartJson;

    const source = String(chartJson == null ? '' : chartJson);
    try {
        return JSON.parse(source);
    } catch (error) {
        const sanitized = removeInvalidJsonEscapes(source);
        if (sanitized === source) throw error;
        return JSON.parse(sanitized);
    }
}

function valueFromPoint(point, fieldName, fallbackName) {
    if (!isPlainObject(point)) return undefined;
    if (Object.prototype.hasOwnProperty.call(point, fallbackName)) {
        return point[fallbackName];
    }
    if (fieldName && Object.prototype.hasOwnProperty.call(point, fieldName)) {
        return point[fieldName];
    }
    return undefined;
}

function normalizeLightweightChartType(type) {
    if (type === 'line') return 'scatter';
    return type || 'bar';
}

export function normalizeChartSpec(spec) {
    if (!isPlainObject(spec) || !Array.isArray(spec.data)) return spec;

    const rows = spec.data.filter(isPlainObject);
    if (rows.length === 0) return spec;

    const firstRow = rows[0];
    const rowAlreadyLooksLikeTrace = Array.isArray(firstRow.x)
        || Array.isArray(firstRow.y)
        || Array.isArray(firstRow.values)
        || Object.prototype.hasOwnProperty.call(firstRow, 'mode')
        || Object.prototype.hasOwnProperty.call(firstRow, 'xaxis')
        || Object.prototype.hasOwnProperty.call(firstRow, 'yaxis');

    if (rowAlreadyLooksLikeTrace) return spec;

    const xValues = rows.map(point => valueFromPoint(point, spec.x, 'x'));
    const yValues = rows.map(point => valueFromPoint(point, spec.y, 'y'));
    const hasChartValues = xValues.some(value => value !== undefined)
        && yValues.some(value => value !== undefined);

    if (!hasChartValues) return spec;

    const normalizedType = normalizeLightweightChartType(spec.type);
    const trace = {
        type: normalizedType,
        x: xValues,
        y: yValues,
    };

    if (spec.type === 'line' && !spec.mode) {
        trace.mode = 'lines+markers';
    } else if (spec.mode) {
        trace.mode = spec.mode;
    }

    const sourceLayout = isPlainObject(spec.layout) ? spec.layout : {};
    const layout = Object.assign({}, sourceLayout, {
        title: spec.title || sourceLayout.title,
        xaxis: Object.assign({}, sourceLayout.xaxis || {}, {
            title: (sourceLayout.xaxis && sourceLayout.xaxis.title) || { text: spec.x || 'x' },
        }),
        yaxis: Object.assign({}, sourceLayout.yaxis || {}, {
            title: (sourceLayout.yaxis && sourceLayout.yaxis.title) || { text: spec.y || 'y' },
        }),
    });

    return Object.assign({}, spec, {
        data: [trace],
        layout,
    });
}

export function humanizeChartSpec(spec) {
    if (!spec || typeof spec !== 'object') return spec;

    const data = Array.isArray(spec.data)
        ? spec.data.map(trace => {
            if (!trace || typeof trace !== 'object') return trace;
            const formattedTrace = Object.assign({}, trace);
            if (typeof trace.name === 'string') {
                formattedTrace.name = humanizeSnakeCaseText(trace.name);
            }
            return formattedTrace;
        })
        : spec.data;

    const sourceLayout = spec.layout && typeof spec.layout === 'object'
        ? spec.layout
        : {};
    const layout = Object.assign({}, sourceLayout, {
        title: humanizeTitle(sourceLayout.title),
        xaxis: humanizeAxis(sourceLayout.xaxis),
        yaxis: humanizeAxis(sourceLayout.yaxis),
        legend: sourceLayout.legend && typeof sourceLayout.legend === 'object'
            ? Object.assign({}, sourceLayout.legend, {
                title: humanizeTitle(sourceLayout.legend.title),
            })
            : sourceLayout.legend,
    });

    return Object.assign({}, spec, { data, layout });
}
